import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { toast } from "sonner";
import {
  Terminal, Database, HardDrive, Cpu, Trash2, Zap, Users, AlertTriangle,
  CheckCircle, GitBranch, Loader2, Play,
} from "lucide-react";

const ICON_MAP = {
  trash: Trash2,
  zap: Zap,
  database: Database,
  users: Users,
  "hard-drive": HardDrive,
  cpu: Cpu,
  "alert-triangle": AlertTriangle,
  "check-circle": CheckCircle,
  "git-branch": GitBranch,
};

export default function AdminSystem() {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [output, setOutput] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/admin/terminal/commands")
      .then((r) => setCommands(r.data))
      .catch(() => toast.error("Impossible de charger les commandes"))
      .finally(() => setLoading(false));
  }, []);

  const runCommand = async (cmd) => {
    setRunning(cmd);
    setOutput(null);
    try {
      const { data } = await api.post("/admin/terminal", { command: cmd });
      setOutput(data);
      setHistory((prev) => [data, ...prev].slice(0, 20));
      if (data.success) toast.success(`Commande exécutée (${data.duration_ms}ms)`);
      else toast.error("La commande a échoué");
    } catch (err) {
      const errData = { success: false, output: err.response?.data?.error || "Erreur réseau", command: cmd };
      setOutput(errData);
      toast.error("Erreur d'exécution");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Système"
        subtitle="Maintenance, diagnostics et outils serveur"
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="border-slate-200"><CardContent className="p-5 h-24 animate-pulse bg-slate-50" /></Card>
          ))
        ) : (
          commands.map((cmd) => {
            const Icon = ICON_MAP[cmd.icon] || Terminal;
            const isRunning = running === cmd.cmd;
            return (
              <Card key={cmd.cmd} className="border-slate-200 hover:border-blue-200 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      cmd.danger ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-600"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{cmd.label}</p>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">{cmd.cmd}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 rounded-full h-8 text-xs gap-1"
                        onClick={() => runCommand(cmd.cmd)}
                        disabled={!!running}
                      >
                        {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                        Exécuter
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {output && (
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Terminal className="w-4 h-4" />
                Résultat : <code className="text-sm font-mono text-blue-600">{output.command}</code>
              </h2>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                output.success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
              }`}>
                {output.success ? "Succès" : "Échec"} · {output.duration_ms}ms
              </span>
            </div>
            <pre className="bg-slate-900 text-green-400 rounded-xl p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap">
              {output.output || "(aucune sortie)"}
            </pre>
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Historique de session</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-xs px-3 py-2 rounded-lg bg-slate-50">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${h.success ? "bg-green-500" : "bg-red-500"}`} />
                  <code className="font-mono text-slate-700">{h.command}</code>
                  <span className="text-slate-400 ml-auto">{h.duration_ms}ms</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}